import * as ts from 'type-schema';
import {
    category as Category,
    product as Product,
    order as Order,
    orderItem as OrderItem,
    orderItemPending as OrderItemPending,
    dealer as Dealer,
    dealerx as Dealerx,
    date as OrderDate,
    freebieRule as FreebieRule,
    pkg as Pkg,
    pkgItem as PkgItem,
} from 'ka-erp-data';
import {user as User} from '../lib/user';
import * as UserSession from './lib/user';
import {result} from '../lib/result';
import {ItemType, PartialRequired} from '../lib/utils';

const base = window.location.host.includes('localhost') ? 'http://localhost:3000/api' : '/api';

export namespace product {
    type field = ItemType<ReturnType<typeof Product.schema.fields>>;
    
    export async function get<fld extends field>(
        ids: readonly string[],
        fields: readonly fld[],
    ): Promise<PartialRequired<Product, fld>[]> {
        const params = new URLSearchParams();
        ids.forEach(id => params.append('ids', id));
        fields.forEach(f => params.append('fields', f));
        const res = await fetch(`${base}/product?${params.toString()}`);
        return res.json();
    }

    type company = ParamType<typeof Product.query.byCompany>;
    export async function online<fld extends field>(
        fields: fld[],
        company: company,
    ): Promise<PartialRequired<Product, fld>[]> {
        const params = new URLSearchParams();
        fields.forEach(f => params.append('fields', f));
        params.append('company', company);
        const res = await fetch(`${base}/product/online?${params.toString()}`);
        return res.json();
    }
    type ParamType<t extends (arg: any) => any> = t extends (arg: infer r) => any ? r : any;
}

export namespace productCategory {
    export async function get(): Promise<Category[]> {
        const res = await fetch(`${base}/product-category`);
        return res.json();
    }
}

export namespace order {
    type field = ItemType<ReturnType<typeof Order.schema.fields>>;

    export async function getByDateRange<fld extends field>(
        fields: readonly fld[],
        customer: string,
        start: OrderDate,
        end: OrderDate,
    ): Promise<PartialRequired<Order, fld>[]> {
        const params = new URLSearchParams();
        fields.forEach(f => params.append('fields', f));
        params.append('customer', customer);
        params.append('start', OrderDate.toNum(start).toString());
        params.append('end', OrderDate.toNum(end).toString());
        const res = await fetch(`${base}/order/byDateRange?${params.toString()}`);
        return res.json();
    }

    export async function get<fld extends field>(
        fields: readonly fld[],
        ids: readonly string[],
    ): Promise<PartialRequired<Order, fld>[]> {
        if (ids.length === 0) {
            return [];
        }
        const params = new URLSearchParams();
        fields.forEach(f => params.append('fields', f));
        ids.forEach(id => params.append('ids', id));
        const res = await fetch(`${base}/order?${params.toString()}`);
        return res.json();
    }

    /** 回傳訂單編號[] */
    export async function post(
        customer: string,
        receiver: string | null,
        deliverDate: OrderDate | null,
        memo: string | null,
        items: {
            code: string,
            qty: number,
            classCnt: number,
            memo: string | null,
        }[],
        rules: {
            filters: string[],
            mapper: string,
        }[],
        productIdByCode: {[code: string]: string},
    ): Promise<string[]> {
        const user = UserSession.load();
        if (user) {
            const creator = user.email;
            const ddate = deliverDate ? OrderDate.toNum(deliverDate): null;
            const res = await fetch(`${base}/order`, {
                method: 'post',
                body: JSON.stringify({creator, customer, receiver, deliverDate: ddate, memo, items, rules, productIdByCode}),
                headers: {
                    'Content-Type': 'application/json',
                },
            });
            return res.json();
        } else {
            return [];
        }
    }
}

export namespace orderItem {
    type field = ItemType<ReturnType<typeof OrderItem.schema.fields>>;

    export async function get<fld extends field>(
        fields: readonly fld[],
        orders: readonly number[],
    ): Promise<PartialRequired<OrderItem, fld>[]> {
        if (orders.length === 0) {
            return [];
        }
        const params = new URLSearchParams();
        fields.forEach(f => params.append('fields', f));
        orders.forEach(o => params.append('orders', o.toString()));
        const res = await fetch(`${base}/order-item?${params.toString()}`);
        return res.json();
    }
}

export namespace orderItemPending {
    type field = ItemType<ReturnType<typeof OrderItemPending.schema.fields>>;

    export async function getPending<fld extends field>(
        fields: fld[],
        customer: string,
    ): Promise<PartialRequired<OrderItemPending, fld>[]> {
        const params = new URLSearchParams();
        fields.forEach(f => params.append('fields', f));
        params.append('customer', customer);
        const res = await fetch(`${base}/order-item-pending/pending?${params.toString()}`);
        return res.json();
    }

    export async function getByOriOrder<fld extends field>(
        fields: fld[],
        orders: string[],
    ): Promise<PartialRequired<OrderItemPending, fld>[]> {
        const params = new URLSearchParams();
        fields.forEach(f => params.append('fields', f));
        orders.forEach(o => params.append('oids', o));
        const res = await fetch(`${base}/order-item-pending?${params.toString()}`);
        return res.json();
    }
}

export namespace pkg {
    type field = ItemType<ReturnType<typeof Pkg.schema.fields>>;
    type itemField = ItemType<ReturnType<typeof PkgItem.schema.fields>>;
    export async function get<fld extends field, ifld extends itemField>(
        ids: string[],
        fields: fld[],
        itemFields: ifld[],
    ): Promise<(PartialRequired<Pkg, fld> & {items: PartialRequired<PkgItem, ifld>[]})[]> {
        const params = new URLSearchParams();
        ids.forEach(id => params.append('ids', id));
        fields.forEach(f => params.append('fields', f));
        itemFields.forEach(f => params.append('itemFields', f));
        const res = await fetch(`${base}/pkg?${params.toString()}`);
        return res.json();
    }
}

export namespace dealer {
    type member = ItemType<Dealerx['members']>;

    export async function load(dealer: string): Promise<member[]> {
        const res = await fetch(`${base}/dealer/${dealer}/members`);
        return res.json();
    }

    export async function save(dealer: string, members: member[]): Promise<result<member[]>> {
        const res = await fetch(`${base}/dealer/${dealer}/members`, {
            method: 'put',
            body: JSON.stringify(members),
            headers: {
                'Content-Type': 'application/json',
            },
        });
        if (res.status === 500) {
            return ['伺服器錯誤', null];
        } else {
            return [null, members];
        }
    }

    type field = ItemType<ReturnType<typeof Dealer.schema.fields>>;
    export async function get<fld extends field>(
        fields: fld[],
        ids: string[],
    ): Promise<PartialRequired<Dealer, fld>[]> {
        if (ids.length === 0) {
            return [];
        } else {
            const params = new URLSearchParams();
            fields.forEach(f => params.append('fields', f));
            ids.forEach(id => params.append('ids', id));
            const res = await fetch(`${base}/dealer?${params.toString()}`);
            return res.json();
        }
    }

    export async function receiver<fld extends field>(
        customer: string,
        fields: fld[],
    ): Promise<PartialRequired<Dealer, fld>[]> {
        const params = new URLSearchParams();
        fields.forEach(f => params.append('fields', f));
        const res = await fetch(`${base}/dealer/${customer}/receiver?${params.toString()}`);
        return res.json();
    }

    export async function queryByEmail(email: string): Promise<Dealerx[]> {
        const params = new URLSearchParams();
        params.append('email', email);
        const res = await fetch(`${base}/dealerx?${params.toString()}`);
        return res.json();
    }
}

export namespace user {
    export async function get(idToken: string): Promise<result<User>> {
        const params = new URLSearchParams();
        params.append('idToken', idToken);
        const _res = await fetch(`${base}/user?${params.toString()}`);
        const res: Res = await _res.json();
        if (res.error) {
            return [res.error, null];
        } else {
            return ts.transform(User.schema, res.data);
        }

        type Res = {
            error: string | null,
            data: ts.buildType<typeof User.schema, false> | null,
        }
    }
}

export namespace freebieRule {
    export async function load(): Promise<rule[]> {
        const res = await fetch(`${base}/freebie-rule`);
        return res.json();
    }

    type rule = {
        filters: string[],
        mapper: string,
    }
}
